import { Box, Card, Chip, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import {
  FORM_STATUS_COLOR,
  formProgress,
  type FormItem,
} from '@/cam-admin/control-panel/fixtures';

// ── Shared bits ────────────────────────────────────────────────────────────

const StatusChip = ({ form }: { form: FormItem }) => (
  <Chip
    size='small'
    label={form.status}
    color={FORM_STATUS_COLOR[form.status]}
  />
);

const FormIcon = ({ icon, lead }: { icon: string; lead?: boolean }) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      flexShrink: 0,
      borderRadius: '50%',
      // Coral for the lead, the same two steps ClaimRow uses on the member
      // side for the one to do first.
      bgcolor: lead ? 'custom.coral.50' : 'custom.limeGreen.200',
      color: lead ? 'custom.coral.500' : 'primary.main',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Icon icon={icon as never} fontSize={18} />
  </Box>
);

// ── List ─────────────────────────────────────────────────────────────────────

const Progress = ({ form }: { form: FormItem }) => {
  const { answered, total } = formProgress(form);
  return (
    <Typography variant='caption' color='text.secondary'>
      {answered === 0 ? 'Not started' : `${answered} of ${total} answered`}
    </Typography>
  );
};

const FormRow = ({ form, onOpen }: { form: FormItem; onOpen: () => void }) => (
  // `listItem` is llama's press state: pointer, a 120ms transform and a
  // scale(0.98) on active. This row used to fill Lime Green 100 on hover and
  // draw a Marine Green border, which was written here and nowhere else in CAM
  // — a list of forms turning green under the cursor for no reason the rest of
  // the workspace gave.
  <Card
    variant='listItem'
    onClick={onOpen}
    sx={{
      p: 2,
      ...(form.lead ? { borderColor: 'custom.coral.500' } : {}),
    }}
  >
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
      <FormIcon icon={form.icon} lead={form.lead} />
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.75}>
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
          spacing={1.5}
        >
          <Typography variant='h6' noWrap>
            {form.name}
          </Typography>
          <StatusChip form={form} />
        </Stack>
        <Typography variant='body2' color='text.secondary'>
          {form.description}
        </Typography>
        <Progress form={form} />
      </Stack>
    </Stack>
  </Card>
);

// ── Tab ──────────────────────────────────────────────────────────────────────

/**
 * Forms the care circle completes in their app, as a list.
 *
 * Opening one is FormDrawer's job, over the workspace. This used to swallow the
 * tab with a page of its own, back link and all, which made a form somewhere
 * you went rather than something you looked at while working on a circle. It
 * also drew its own review, with an Edit on every row that swapped to Save and
 * Cancel, and answered counts per section: a third way of editing a record in
 * one workspace.
 *
 * The answers live in ControlPanel with the actions, the calls and the
 * documents, because the drawer that edits them is rendered up there.
 */
const FormsTab = ({
  forms,
  onOpen,
}: {
  forms: FormItem[];
  onOpen: (id: string) => void;
}) => (
  <Stack spacing={2}>
    {/* The lead first, whatever order the fixture is in. It says which claims
        are worth doing at all, so it is where you start rather than one of four
        things to get through. */}
    {[...forms]
      .sort((a, b) => Number(b.lead ?? false) - Number(a.lead ?? false))
      .map((form) => (
        <FormRow key={form.id} form={form} onOpen={() => onOpen(form.id)} />
      ))}
  </Stack>
);

export default FormsTab;
